import { Col, ColProps, Divider, Form, Row, Typography } from 'antd';
import { FieldData } from 'rc-field-form/es/interface';
import React, { FC, Fragment, useEffect, useState } from 'react';

import { JCMSCTA, JCMSCTAProps } from '../../JCMSCTA';
import { useJCommonContext } from '../../JCommonProvider';
import { JHTML } from '../../JHTML';
import { DEFAULT_ROW_GUTTER } from '../constants';
import {
    FormFieldActionEvent,
    FormFieldOnAction,
    FormOutput,
    OnSubmitErrorForm,
    OnSubmitForm,
    OnSubmitRawData,
    Output,
    OverrideColsProps,
    RenderAfterFieldProps
} from './../models';
import { JFieldDTO } from './../models/overrideBE';
import { getColConfigFromFieldDTO, isValue, onFinishForm, recursiveSort } from './../utils';
import { useCompositorContext } from './CompositorProvider';
import { FormList } from './FormList';
import { MapFieldComponent } from './MapFieldComponent';

export type JComposedProps = CoreCompositorProps
export const JComposed: FC<JComposedProps> = (props) => {
    return (
        <div className={`form-compositor-children form-compositor-children-${props.formId}`}>
            <CoreCompositor
                {...props}
            />
        </div>
    )
}

export interface CoreCompositorProps {
    /**
     * Form ID
     *
     * @type {string}
     * @memberof CoreCompositorProps
     */
    formId: string
    /**
     * List of fields for render
     *
     * @type {JFieldDTO[]}
     * @memberof CoreCompositorProps
     */
    fields: JFieldDTO[]
    /**
     * Title to render
     *
     * @type {string}
     * @memberof CoreCompositorProps
     */
    title?: string
    /**
     * Submit payload callback
     *
     * @type {OnSubmitForm}
     * @memberof CoreCompositorProps
     */
    onSubmit?: OnSubmitForm
    /**
     * Submit raw form output
     *
     * @type {onSubmitRawData}
     * @memberof CoreCompositorProps
     */
    onSubmitRawData?: OnSubmitRawData
    /**
     * Submit error callback
     *
     * @type {OnSubmitErrorForm}
     * @memberof CoreCompositorProps
     */
    onSubmitError?: OnSubmitErrorForm
    /**
     * onFieldsChange Form callback
     *
     * @memberof CoreCompositorProps
     */
    onFieldsChange?: (changedFields: FieldData[], allFields: FieldData[]) => void
    /**
     * list of Form parent
     *
     * @type {string[]}
     * @memberof CoreCompositorProps
     */
    parentNames?: (string | number)[],
    /**
     * Defines if the compositor is wrapper of Form component
     *
     * @type {boolean}
     * @memberof CoreCompositorProps
     */
    withoutFormComponent?: boolean,
    /**
     * Before submit component
     *
     * @type {JSX.Element}
     * @memberof CoreCompositorProps
     */
    beforeSubmit?: JSX.Element
    /**
     * After submit component
     *
     * @type {JSX.Element}
     * @memberof CoreCompositorProps
     */
    afterSubmit?: JSX.Element,
    /**
     * List of submit actions (ex. buttons)
     *
     * @type {JSX.Element}
     * @memberof CoreCompositorProps
     */
    customActions?: JSX.Element
    /**
     * Callback for user interaction
     *
     * @type {JSX.FormFieldOnAction}
     * @memberof CoreCompositorProps
     */
    onAction?: FormFieldOnAction,
    /**
     * Override fields cols by type or hurlId
     *
     * @type {OverrideColsProps}
     * @memberof CoreCompositorProps
     */
    overrideColsProps?: OverrideColsProps,
    /**
     * Render jsx element after field by type or hurlId
     *
     * @type {RenderAfterFieldProps}
     * @memberof CoreCompositorProps
     */
    renderAfterField?: RenderAfterFieldProps,
    /**
     * List of cta
     * @description SCOPE = 'SUBMIT' for type submit. SCOPE = 'RESET' for type reset
     * @type {JCMSCTAProps[]}
     * @memberof CoreCompositorProps
     */
    cta?: JCMSCTAProps[]
    /**
     * Disabled all fields
     *
     * @type {boolean}
     * @memberof CoreCompositorProps
     */
    disableFields?: boolean;
    /**
     * Disabled all cta
     *
     * @type {boolean}
     * @memberof CoreCompositorProps
     */
    disableCTA?: boolean;
    /**
     * Disabled all cta and fields
     *
     * @type {boolean}
     * @memberof CoreCompositorProps
     */
    disableForm?: boolean;

    disableLoading?: boolean;

    options?: any;
}

export const CoreCompositor: FC<CoreCompositorProps> = (props) => {
    const { translate, uploadFiles } = useCompositorContext()
    const { setLoading: jSetLoading } = useJCommonContext()
    const parentNames = props.parentNames ? props.parentNames : ['data']

    const [components, setComponents] = useState<JSX.Element[]>([])
    const [error, setError] = useState<boolean>(false)

    function checkError(fields: JFieldDTO[]): boolean {
        if(fields){
            for (const field of fields) {
                if (!field.cmsConfig || !field.hurlId) {
                    return true
                }
                if (field.children?.length) {
                    return checkError(field.children)
                }
            }
        }
        return false
    }

    useEffect(() => {
        const err = checkError(props.fields)
        if (err) {
            setError(true)
        } else {
            recursiveSort(props.fields)
            const _components = buildForm(props.fields)
            setComponents(_components)
        }
    }, [props.fields, props.renderAfterField, props.overrideColsProps])

    /**
     * Gets value based on type
     *
     * @param {string[]} value
     * @param {string} type
     * @return {*}
     */
    function getFieldDTOValue(value: string[], type: string) {
        switch (type) {
            default:
                return value?.[0]
        }
    }

    function withCol(element: JSX.Element, field: JFieldDTO): JSX.Element {
        const colProps: ColProps & Record<string, any> = (
            props.overrideColsProps?.[field.hurlId] ||
            props.overrideColsProps?.[field.type] ||
            props.overrideColsProps?.DEFAULT ||
            field.cmsConfig.colConfig ||
            getColConfigFromFieldDTO(field)
        )
        const _props = colProps
        return <Col {..._props} >{element}</Col>
    }
    /**
     * Get component based on field type
     *
     * @param {JFieldDTO} field
     * @return {JSX.Element}
     */
    function buildFieldComponent(field: JFieldDTO): JSX.Element {
        const inputProps: Record<string, any> = {}
        if (isValue(field.values)) {
            inputProps.defaultValue = getFieldDTOValue(field.values, field.type)
        }
        const formItemName = [...parentNames, field.hurlId]
        const id = props.formId + field.hurlId
        if (field.repeateble) {
            return (
                <FormList name={formItemName} field={field}>
                    {
                        (_field, name, listname) => {
                            if (_field.children?.length) {
                                return (
                                    <JComposed
                                        formId={id}
                                        disableFields={props.disableFields}
                                        disableForm={props.disableForm}
                                        title={_field.cmsConfig.title}
                                        fields={_field.children}
                                        withoutFormComponent={true}
                                        parentNames={name}
                                    />
                                )
                            }
                            return (
                                <MapFieldComponent
                                    field={_field}
                                    formItemName={name}
                                    onAction={onAction}
                                    fullname={listname}
                                    disabled={props.disableForm || props.disableFields}
                                />
                            )
                        }
                    }
                </FormList>
            )


        }
        return (
            <Fragment>
                {
                    field.children?.length ? (
                        <JComposed
                            formId={id}
                            disableFields={props.disableFields}
                            disableForm={props.disableForm}
                            title={field.cmsConfig.title}
                            fields={field.children}
                            withoutFormComponent={true}
                            parentNames={[...parentNames, field.hurlId]}

                        />
                    ) : (
                        <MapFieldComponent
                            field={field}
                            formItemName={formItemName}
                            onAction={onAction}
                            disabled={props.disableForm || props.disableFields}
                        />
                    )
                }
            </Fragment>
        )
    }
    function onAction(event: FormFieldActionEvent) {
        if (props.onAction) props.onAction(event)
    }
    function buildForm(fieldDTOs: JFieldDTO[]): any[] {

        const _components = []
        for (const field of fieldDTOs) {

            const key = props.formId + field.hurlId
            _components.push(
                <Fragment key={key}>
                    {
                        withCol(buildFieldComponent(field), field)
                    }
                </Fragment>
            )

            if (props.renderAfterField) {
                const render = props.renderAfterField[field.type] || props.renderAfterField[field.hurlId]
                if (render && render.element) {
                    const colProps = render.colProps || { span: 24 }
                    _components.push(
                        <Col key={`${key}-render-element`} {...colProps}>{render.element}</Col>
                    )
                }
            }

        }
        return _components
    }
    const onFieldsChange = (changedFields: FieldData[], allFields: FieldData[]) => {
        if (props.onFieldsChange) props.onFieldsChange(changedFields, allFields)
    }
    const setLoading = (value: boolean) => {
        if (!props.disableLoading) {
            jSetLoading(value)
        }
    }

    const trimmedObjValue = (value: Output): Output => {
         Object.keys(value).forEach((key: string) => {
            if(value[key] && (typeof value[key] === 'string')){
                value[key] = (value[key] as string).trim();
            }
        })
        return value;
    }
    const onFinish = async (output: FormOutput) => {

        const data = output?.data ? trimmedObjValue(output?.data) : {}
        const confirms =  output.formCompositorConfirms?.data ? trimmedObjValue(output.formCompositorConfirms?.data) : {}

        if (props.onSubmitRawData) {
            props.onSubmitRawData(data, confirms)
        }
        if (props.onSubmit) {
            try {
                setLoading(true)
                const payload = await onFinishForm({ fields: props.fields, data, uploadFiles })
                await props.onSubmit(payload, confirms)
            } catch (error) {
                if (props.onSubmitError) {
                    props.onSubmitError(error as Error)
                } else {
                    console.error(error)
                }
            } finally {
                setLoading(false)
            }
        }
    }

    if (error) return <Typography.Text>{translate('INVALID_FORM_CONFIGURATION')}</Typography.Text>

    const showCTA = !props.customActions && (props.onSubmit || props.onSubmitRawData)

    return (
        <div className={`form-compositor form-compositor-${props.formId}`}>
            {
                props.title && (
                    <Divider orientation="left" orientationMargin={20}>
                        <Typography.Title level={5}>{props.title}</Typography.Title>
                    </Divider>
                )
            }
            {
                props.withoutFormComponent ? (
                    <Row gutter={DEFAULT_ROW_GUTTER} className='dispRow' style={{ marginBottom: '40px' }}>
                        {components}
                    </Row>
                ) : (
                    <Form onFinish={onFinish} onFieldsChange={onFieldsChange}>
                        <Row gutter={DEFAULT_ROW_GUTTER}>
                            {components}
                        </Row>
                        {
                            props.beforeSubmit && (
                                <div className='form-compositor-before-submit'>
                                    {props.beforeSubmit}
                                </div>
                            )
                        }
                        {
                            props.customActions && (
                                <div className='form-compositor-custom-actions'>
                                    {props.customActions}
                                </div>
                            )
                        }
                        {
                            showCTA && (

                                <div className='form-compositor-actions'>
                                    {
                                        props.cta?.map((cta, i) => (
                                            <JCMSCTA key={i} {...cta} disabled={props.disableForm || props.disableCTA} />
                                        ))
                                    }
                                </div>
                            )
                        }
                        {
                            props.afterSubmit && (
                                <div className='form-compositor-after-submit'>
                                    {props.afterSubmit}
                                </div>
                            )
                        }
                        {
                            props?.options?.['show_disclaimer_required_fields'] && (
                                <div className='show_disclaimer_required_container'>
                                    <JHTML.Text html={translate('REQUIRED_FIELDS_DISCLAIMER')}/>
                                </div>
                            )
                        }
                    </Form>

                )
            }
        </div>
    )
}
